import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { routes } from '@const/routes';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiOrderService } from '@api';
import {
    TTransaction,
    ETransactionAction,
    ETransactionStatus,
    ETransactionType
} from '@xeppt/xeppt-sdk/types';
import { useDebounceValue } from 'usehooks-ts';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import Status from '@components/common/status';
import Skeleton from '@components/common/skeleton';
import Button from '@components/common/button';
import { prettifyAmount, formatDate } from '@utils/index';
import DevPageHeader from '../_shared/PageHeader';
import FilterDrawer, { type MoneyMovementValue, type SortDir } from '../_shared/FilterDrawer';
import PurchaseDetailDrawer, { PurchaseDetailOrderInfo } from '../_shared/PurchaseDetailDrawer';
import styles from './styles.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

type TSaleTransaction = TTransaction & {
    order_id?: string;
    description?: string;
    points?: number;
};

type FetchParams = {
    isNew?: boolean;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    statuses?: ETransactionStatus[];
    movement?: MoneyMovementValue;
    sortDir?: SortDir;
};

type SalesResponse = {
    data: TSaleTransaction[];
    page: number;
    page_size: number;
    pages_total: number;
    total?: number;
    total_received: number;
    total_approved: number;
    total_failed: number;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

const SALES_STATUS_CHIPS: { label: string; value: ETransactionStatus }[] = [
    { label: 'Approved', value: ETransactionStatus.APPROVED },
    { label: 'Pending', value: ETransactionStatus.PENDING },
    { label: 'Canceled', value: ETransactionStatus.CANCELED },
    { label: 'Declined', value: ETransactionStatus.DECLINED }
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function addDatetime(date: string, time: string): string {
    return `${date}T${time}.000Z`;
}

// The backend's `page` response field is a pure echo of the request — never
// server-incremented — so trusting it (via a `data.page`-derived
// `currentPage` state) made infinite scroll stick on page 1 forever: every
// "load more" re-requested the exact page it had just received. The next
// page must instead be derived from how many rows are already loaded. A
// missing request (the initial mount fetch) or an explicit `isNew` (filter
// apply/retry/search) both mean "start over from page 1"; anything else is
// a genuine load-more continuation from the scroll observer. Exported so
// this fix is independently regression-tested.
export function isFreshPageRequest(req?: FetchParams): boolean {
    return !req || !!req.isNew;
}

// See isFreshPageRequest above — never derive the next page from the
// backend's echoed `page` field.
export function computeNextPage(req: FetchParams | undefined, loadedCount: number): number {
    return isFreshPageRequest(req) ? 1 : Math.floor(loadedCount / PAGE_SIZE) + 1;
}

function parseSource(source: string): { displayName: string; handle: string; initials: string } {
    const match = /^(.*?)\s*\((@[^)]+)\)$/.exec(source);
    if (!match) {
        return { displayName: source, handle: '', initials: source.slice(0, 2).toUpperCase() };
    }
    const name = match[1].trim();
    const parts = name.split(/\s+/);
    const initials =
        parts.length >= 2
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : name.slice(0, 2).toUpperCase();
    return { displayName: name, handle: match[2], initials };
}

export function txnSecondaryLabel(tx: TTransaction): string {
    // `action` is the authoritative discriminator for "this row IS a refund"
    // (a refund leg is Action=REFUND/Type=CREDIT; a sale leg is
    // Action=PAYMENT/Type=DEBIT) — checked first, same convention as the
    // canonical transactions_table/transaction_item.tsx renderer. `status`
    // alone is not reliable here: RETURNED still only decorates the
    // ORIGINAL sale row once its order has since been refunded.
    if (tx.action === ETransactionAction.REFUND) return 'Refund';
    if (tx.status === ETransactionStatus.RETURNED) return 'Purchase. Refunded';
    return 'Purchase';
}

export function isRefundTxn(tx: TTransaction): boolean {
    return tx.action === ETransactionAction.REFUND;
}

function txnStatusLabel(status: ETransactionStatus): string {
    switch (status) {
        case ETransactionStatus.PENDING: return 'Pending';
        case ETransactionStatus.APPROVED: return 'Approved';
        case ETransactionStatus.DECLINED: return 'Declined';
        case ETransactionStatus.CANCELED: return 'Canceled';
        case ETransactionStatus.REFUND: return 'Refund';
        case ETransactionStatus.ERROR: return 'Error';
        case ETransactionStatus.RETURNED: return 'Returned';
        case ETransactionStatus.EXPIRED: return 'Expired';
        default: return 'Unknown';
    }
}

function txnStatusVariant(status: ETransactionStatus): 'success' | 'warning' | 'error' | 'gray' {
    switch (status) {
        case ETransactionStatus.APPROVED: return 'success';
        case ETransactionStatus.PENDING: return 'warning';
        case ETransactionStatus.DECLINED:
        case ETransactionStatus.ERROR:
        case ETransactionStatus.CANCELED: return 'error';
        default: return 'gray';
    }
}

function shouldShowStatusBadge(status: ETransactionStatus): boolean {
    return (
        status === ETransactionStatus.PENDING ||
        status === ETransactionStatus.DECLINED ||
        status === ETransactionStatus.CANCELED
    );
}

// Builds the lightweight order-info bag PurchaseDetailDrawer needs, straight
// from the already-fetched list row — no second network request. Sales rows
// are always order-linked (findBusinessSales is the same order-driven
// endpoint Orders itself uses), so order_id/description are expected to be
// present, but the drawer degrades gracefully if either is missing.
function saleTxnOrderInfo(txn: TSaleTransaction): PurchaseDetailOrderInfo {
    const { displayName, handle } = parseSource(txn.source ?? '');
    return {
        customer_display_name: displayName || undefined,
        customer_id: handle.replace(/^@/, '') || undefined,
        currency: txn.currency,
        order_id: txn.order_id,
        description: txn.description
    };
}

// ─── Layout ──────────────────────────────────────────────────────────────────

const DevelopersSalesLayout = () => {
    const navigate = useNavigate();
    const { user } = useUserContext();
    const { env } = useEnvironmentContext();

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch] = useDebounceValue(searchInput, 300);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [movement, setMovement] = useState<MoneyMovementValue>('all');
    const [statusChips, setStatusChips] = useState<ETransactionStatus[]>([]);
    const [sortDir, setSortDir] = useState<SortDir>('newest');

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedTxnEntry, setSelectedTxnEntry] = useState<TSaleTransaction | null>(null);

    const [transactions, setTransactions] = useState<TSaleTransaction[]>([]);
    const [trigger, setTrigger] = useState(0);
    const loader = useRef<HTMLDivElement>(null);

    const {
        data: salesResponse,
        isLoading,
        error,
        handleRequest: fetchSales
    } = useApiQuery<FetchParams, SalesResponse>({
        method: (req?: FetchParams) => {
            const page = computeNextPage(req, transactions.length);
            let typeVal: ETransactionType | undefined;
            if (req?.movement === 'in') typeVal = ETransactionType.CREDIT;
            else if (req?.movement === 'out') typeVal = ETransactionType.DEBIT;
            const orderVal = req?.sortDir === 'oldest' ? 'ASC' : 'DESC';
            return apiOrderService.findBusinessSales({
                page,
                page_size: PAGE_SIZE,
                order: orderVal,
                search: req?.search || undefined,
                date_from: req?.dateFrom ? addDatetime(req.dateFrom, '00:00:00') : undefined,
                date_to: req?.dateTo ? addDatetime(req.dateTo, '23:59:59') : undefined,
                status:
                    req?.statuses && req.statuses.length > 0 ? req.statuses : undefined,
                type: typeVal !== undefined ? [typeVal] : undefined,
                environment: env
            }) as unknown as Promise<SalesResponse>;
        },
        onSuccess: (data, req) => {
            const incoming = (data.data ?? []) as TSaleTransaction[];
            if (isFreshPageRequest(req)) {
                setTransactions(incoming);
            } else {
                setTransactions((prev) => [...prev, ...incoming]);
            }
        },
        condition: !!user,
        deps: [user, env]
    });

    const applyFetch = useCallback(
        (overrides?: Partial<FetchParams>) => {
            fetchSales({
                isNew: true,
                search: debouncedSearch,
                dateFrom,
                dateTo,
                movement,
                statuses: statusChips,
                sortDir,
                ...overrides
            });
        },
        [fetchSales, debouncedSearch, dateFrom, dateTo, movement, statusChips, sortDir]
    );

    // Opens the shared merchant transaction drawer instead of the customer-facing
    // VIEW_TRANSACTION modal. The row object already has everything the drawer
    // needs (id/type/amount/status/created_at/points/order_id/description/source)
    // so no re-fetch is needed. Mirrors the mutual-exclusion pattern in
    // ../orders/index.tsx: opening the drawer closes the filter panel first.
    const handleViewTxn = (txn: TSaleTransaction) => {
        setIsFilterOpen(false);
        setSelectedTxnEntry(txn);
    };

    useEffect(() => {
        if (debouncedSearch.length === 0 || debouncedSearch.length >= 3) {
            applyFetch({ search: debouncedSearch });
        }
    }, [debouncedSearch]);

    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) setTrigger((prev) => prev + 1);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '20px',
            threshold: 0
        });
        if (loader.current) observer.observe(loader.current);
    }, [handleObserver]);

    useEffect(() => {
        const total = salesResponse?.total ?? 0;
        if (transactions.length < total && !isLoading) {
            fetchSales({ search: debouncedSearch, dateFrom, dateTo, movement, statuses: statusChips, sortDir });
        }
    }, [trigger]);

    const handleFilterClose = () => {
        setIsFilterOpen(false);
        applyFetch();
    };

    const handleFilterReset = () => {
        setDateFrom('');
        setDateTo('');
        setMovement('all');
        setStatusChips([]);
        setSortDir('newest');
    };

    const handleToggleStatus = (s: ETransactionStatus) => {
        setStatusChips((prev) =>
            prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
        );
    };

    const activeFilterCount =
        (dateFrom || dateTo ? 1 : 0) +
        (movement !== 'all' ? 1 : 0) +
        (statusChips.length > 0 ? 1 : 0);

    const stats = [
        {
            title: 'Total received',
            helper: 'Total amount of successful customer purchases in the selected period',
            value: `$${prettifyAmount((salesResponse?.total_received ?? 0).toFixed(2))}`,
            icon: 'money' as const,
            mod: styles.stat_received
        },
        {
            title: 'Successful purchases',
            helper: 'Sum of approved purchases in the selected period',
            value: String(salesResponse?.total_approved ?? 0),
            icon: 'check_outlined' as const,
            mod: styles.stat_success
        },
        {
            title: 'Failed purchases',
            helper: 'Number of failed or declined purchases in the selected period',
            value: String(salesResponse?.total_failed ?? 0),
            icon: 'error_outlined' as const,
            mod: styles.stat_error
        }
    ];

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <DevPageHeader
                title="Sales"
                subtitle="View payment transactions processed through your XEPPT integration."
            />
            <div className={styles.stats}>
                {isLoading && !transactions.length ? (
                    <>
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                    </>
                ) : (
                    stats.map((item) => (
                        <div key={item.title} className={[styles.stat_card, item.mod].join(' ')}>
                            <div className={styles.stat_icon}>
                                <Icon name={item.icon} width={22} height={22} />
                            </div>
                            <div className={styles.stat_body}>
                                <div className={styles.stat_label_row}>
                                    <p className={styles.stat_label}>{item.title}</p>
                                    <Info variant="left" content={<>{item.helper}</>} />
                                </div>
                                <Typography variant="h5" weight="semibold" className={styles.stat_value}>
                                    {item.value}
                                </Typography>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className={styles.list_card}>
                <div className={styles.toolbar}>
                    <div className={styles.search_wrap}>
                        <Icon name="search" width={16} height={16} className={styles.search_icon} />
                        <input
                            type="text"
                            placeholder="Search by customer or order ID..."
                            value={searchInput}
                            className={styles.search_input}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                    <div className={styles.toolbar_actions}>
                        <button type="button" className={styles.download_btn}>
                            <Icon name="download" width={16} height={16} />
                            Download
                        </button>
                        <button
                            type="button"
                            className={styles.filters_btn}
                            onClick={() => setIsFilterOpen(true)}>
                            <Icon name="filter" width={16} height={16} />
                            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
                        </button>
                    </div>
                </div>
                <div className={styles.list}>
                    {isLoading && !transactions.length &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={`sk-${i}`} className={styles.row_skeleton}>
                                <Skeleton className={styles.skeleton_avatar} />
                                <div className={styles.skeleton_text_block}>
                                    <Skeleton className={styles.skeleton_line_primary} />
                                    <Skeleton className={styles.skeleton_line_secondary} />
                                </div>
                                <Skeleton className={styles.skeleton_amount} />
                            </div>
                        ))}
                    {!isLoading && !!error && transactions.length === 0 && (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="error_outlined" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                Failed to load sales
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                There was a problem fetching your transactions.
                            </Typography>
                            <Button
                                variant="outlined-primary"
                                size="small"
                                onClick={() => fetchSales({ isNew: true })}>
                                Retry
                            </Button>
                        </div>
                    )}
                    {!isLoading && !error && transactions.length === 0 && (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="money" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                No sales yet
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                Payment transactions will appear here once customers pay.
                            </Typography>
                        </div>
                    )}
                    {transactions.map((txn) => {
                        const { displayName, handle, initials } = parseSource(txn.source ?? '');
                        const refund = isRefundTxn(txn);
                        const secondaryLabel = txnSecondaryLabel(txn);
                        const showBadge = shouldShowStatusBadge(txn.status);
                        return (
                            <div
                                key={txn.id}
                                className={styles.list_row}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleViewTxn(txn)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') handleViewTxn(txn);
                                }}>
                                <div
                                    className={[
                                        styles.row_avatar,
                                        refund ? styles.row_avatar_out : styles.row_avatar_in
                                    ].join(' ')}>
                                    <Icon
                                        name={refund ? 'transaction_arrow_up' : 'transaction_arrow_down'}
                                        width={20}
                                        height={20}
                                    />
                                    {!displayName && (
                                        <span className={styles.row_initials}>{initials}</span>
                                    )}
                                </div>
                                <div className={styles.row_info}>
                                    <p className={styles.row_name}>
                                        {displayName || txn.source}
                                        {handle && (
                                            <span className={styles.row_handle}> {handle}</span>
                                        )}
                                    </p>
                                    <p className={styles.row_secondary}>
                                        {secondaryLabel}
                                        <span className={styles.row_dot}> · </span>
                                        {formatDate(txn.created_at, 'DD MMM YYYY')}
                                    </p>
                                </div>
                                {showBadge && (
                                    <div className={styles.row_badge}>
                                        <Status variant={txnStatusVariant(txn.status)}>
                                            {txnStatusLabel(txn.status)}
                                        </Status>
                                    </div>
                                )}
                                <p
                                    className={[
                                        styles.row_amount,
                                        refund ? styles.row_amount_debit : styles.row_amount_credit
                                    ].join(' ')}>
                                    {refund ? '-' : '+'}$
                                    {prettifyAmount(Number(txn.amount).toFixed(2))}
                                </p>
                            </div>
                        );
                    })}
                    {isLoading && transactions.length > 0 &&
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={`sk-more-${i}`} className={styles.row_skeleton}>
                                <Skeleton className={styles.skeleton_avatar} />
                                <div className={styles.skeleton_text_block}>
                                    <Skeleton className={styles.skeleton_line_primary} />
                                    <Skeleton className={styles.skeleton_line_secondary} />
                                </div>
                                <Skeleton className={styles.skeleton_amount} />
                            </div>
                        ))}
                </div>
            </div>
            <div ref={loader} />
            {isFilterOpen && (
                // Explicit type argument: TStatus mixes a covariant prop
                // (statusChips) with a contravariant one (onToggleStatus),
                // which JSX's generic inference doesn't always resolve to
                // ETransactionStatus on its own — pin it instead of hoping.
                <FilterDrawer<ETransactionStatus>
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    movement={{ value: movement, onChange: setMovement }}
                    statusChips={SALES_STATUS_CHIPS}
                    selectedStatuses={statusChips}
                    sortDir={sortDir}
                    onDateFrom={setDateFrom}
                    onDateTo={setDateTo}
                    onToggleStatus={handleToggleStatus}
                    onSortDir={setSortDir}
                    onReset={handleFilterReset}
                    onClose={handleFilterClose}
                />
            )}
            {selectedTxnEntry && (
                <PurchaseDetailDrawer
                    txn={selectedTxnEntry}
                    order={saleTxnOrderInfo(selectedTxnEntry)}
                    onClose={() => setSelectedTxnEntry(null)}
                    onViewOrder={() => {
                        setSelectedTxnEntry(null);
                        navigate(routes.developers_orders);
                    }}
                />
            )}
        </motion.div>
    );
};

export default DevelopersSalesLayout;
